import { formatTimeSlot } from '@klareospace/pharos-lib-front-utils';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import type { RouteInformationTimeSlotFormInput, SchedulingSectionFormInput } from '../schemas/order-creation.schema';
import { isDateRangeValueInverted } from './time-slot.utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ResolveSchedulingSeedParams {
  currentTimeSlot: DateRangeValue | null;
  /** Whether the Scheduling section is the drawer's currently active/open section. */
  isSchedulingActive: boolean;
  lastSeededSiteId: string | null;
  lastSeededTimeSlot: DateRangeValue | null;
  selectedSiteId: string | null;
}

export interface SchedulingSeedDecision {
  /** Whether the carrier should be reset to "Not assigned" along with the timeslot. */
  resetCarrier: boolean;
  /** Whether a fresh default timeslot should be seeded. */
  seed: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** True when an enabled delivery timeslot has start >= end; an unset timeslot is never inverted. */
export function isSchedulingTimeSlotInverted(scheduling: SchedulingSectionFormInput): boolean {
  if (!scheduling.deliveryTimeSlot) return false;
  return isDateRangeValueInverted(scheduling.deliveryTimeSlot);
}

/** Complete iff a delivery timeslot is set and not inverted (from < to). Carrier is optional and never blocks completion. */
export function isSchedulingComplete(scheduling: SchedulingSectionFormInput): boolean {
  if (!scheduling.deliveryTimeSlot) return false;
  return !isSchedulingTimeSlotInverted(scheduling);
}

/**
 * The one cross-leg constraint checked at submission (not continuously, since it spans both the
 * Route information and Scheduling slices): when a pickup timeslot is set, the delivery timeslot
 * must not end before the pickup starts. Either leg being unset makes the check moot.
 */
export function isCrossLegTimeWindowInverted(
  pickupTimeSlot: RouteInformationTimeSlotFormInput,
  deliveryTimeSlot: DateRangeValue | null,
): boolean {
  if (!pickupTimeSlot.enabled || !pickupTimeSlot.value || !deliveryTimeSlot) return false;
  return deliveryTimeSlot.range.to < pickupTimeSlot.value.range.from;
}

/** Locale-aware "date · from – to" label for the delivery timeslot, reusing the shared timeslot formatter. */
export function formatSchedulingTimeSlotLabel(value: DateRangeValue, locale: string): string {
  return formatTimeSlot({ end: value.range.to, start: value.range.from }, locale) ?? '';
}

/**
 * Decides whether the Scheduling section's delivery timeslot must be (re)seeded with a fresh
 * default, and whether the carrier should be reset alongside it.
 *
 * - The *initial* seed (no timeslot yet) only happens once Scheduling is the active section — "next
 *   available slot" is computed on arrival, not before.
 * - Once seeded, a pickup-site change re-seeds (and resets the carrier) immediately, regardless of
 *   which section is active — otherwise an already-completed, collapsed Scheduling section would
 *   keep showing the previous site's slot/carrier until the user reopens it.
 * - A manually edited timeslot (reference equality against the last auto-seeded value — a user edit
 *   always produces a new object) is never re-seeded, on a site change or otherwise.
 */
export function resolveSchedulingSeedDecision({
  currentTimeSlot,
  isSchedulingActive,
  lastSeededSiteId,
  lastSeededTimeSlot,
  selectedSiteId,
}: ResolveSchedulingSeedParams): SchedulingSeedDecision {
  const isUnedited = currentTimeSlot === null || currentTimeSlot === lastSeededTimeSlot;
  if (!isUnedited) return { resetCarrier: false, seed: false };

  const neverSeeded = currentTimeSlot === null;
  if (neverSeeded) return { resetCarrier: false, seed: isSchedulingActive };

  const siteChanged = selectedSiteId !== lastSeededSiteId;
  if (!siteChanged) return { resetCarrier: false, seed: false };

  return { resetCarrier: true, seed: true };
}
